import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { CodeListFileDto } from "Infrastructure/Api/Api";
import { useState } from "react";
import { useActivateVersionMutation } from "../../Api/Mutations/useActivateVersionMutation";
import { useCodeListFileVersionsQuery } from "../../Api/Queries/useCodeListFileVersionsQuery";

type Props = {
	file: CodeListFileDto;
	open: boolean;
	onClose: () => void;
};

function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleString("cs-CZ", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export const FileVersionHistory: React.FC<Props> = ({
	file,
	open,
	onClose,
}) => {
	const [copiedId, setCopiedId] = useState<number | null>(null);

	const { data, isLoading } = useCodeListFileVersionsQuery(
		file.folderPath,
		file.fileName,
		open,
	);

	const activateMutation = useActivateVersionMutation();

	const versions =
		data?.status === 200
			? [...data.data].sort((a, b) => b.version - a.version)
			: [];

	const handleDownload = (version: CodeListFileDto) => {
		if (version.downloadUrl) {
			const link = document.createElement("a");
			link.href = version.downloadUrl;
			link.download = version.fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const handleCopyUrl = async (version: CodeListFileDto) => {
		if (version.downloadUrl) {
			await navigator.clipboard.writeText(version.downloadUrl);
			setCopiedId(version.codeListFileID);
			setTimeout(() => setCopiedId(null), 1500);
		}
	};

	const handleActivate = (version: CodeListFileDto) => {
		activateMutation.mutate(version.codeListFileID);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="h6">Historie verzí - {file.fileName}</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 200,
						}}
					>
						<CircularProgress />
					</Box>
				) : versions.length === 0 ? (
					<Typography color="text.secondary" align="center" sx={{ py: 4 }}>
						Žádné verze nenalezeny
					</Typography>
				) : (
					<TableContainer>
						<Table size="small" sx={{ tableLayout: "fixed" }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Verze</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Stav</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Velikost</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Nahráno</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Nahrál</TableCell>
									<TableCell sx={{ fontWeight: 600 }} align="right">
										Akce
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{versions.map((version) => (
									<TableRow
										key={version.codeListFileID}
										sx={{
											backgroundColor: version.isActive
												? "rgba(76, 175, 80, 0.08)"
												: "inherit",
											"&:hover": {
												backgroundColor: version.isActive
													? "rgba(76, 175, 80, 0.12)"
													: "action.hover",
											},
										}}
									>
										<TableCell>
											<Chip
												size="small"
												label={`v${version.version}`}
												color={version.isActive ? "success" : "default"}
												variant={version.isActive ? "filled" : "outlined"}
												sx={{ height: 22 }}
											/>
										</TableCell>
										<TableCell>
											{version.isActive ? (
												<Chip
													size="small"
													icon={<CheckCircleIcon />}
													label="Aktivní"
													color="success"
													sx={{ height: 22 }}
												/>
											) : (
												<Chip
													size="small"
													label="Neaktivní"
													variant="outlined"
													sx={{ height: 22 }}
												/>
											)}
										</TableCell>
										<TableCell>{formatFileSize(version.size)}</TableCell>
										<TableCell>{formatDate(version.uploadedAt)}</TableCell>
										<TableCell>{version.uploadedByName || version.uploadedBy}</TableCell>
										<TableCell align="right">
											{!version.isActive && (
												<Tooltip title="Aktivovat tuto verzi">
													<IconButton
														size="small"
														color="primary"
														onClick={() => handleActivate(version)}
														disabled={activateMutation.isPending}
													>
														<PlayArrowIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
											<Tooltip title="Stáhnout">
												<IconButton
													size="small"
													onClick={() => handleDownload(version)}
													disabled={!version.downloadUrl}
												>
													<DownloadIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip
												title={
													copiedId === version.codeListFileID
														? "Zkopírováno"
														: "Kopírovat URL"
												}
											>
												<IconButton
													size="small"
													onClick={() => handleCopyUrl(version)}
													disabled={!version.downloadUrl}
												>
													<ContentCopyIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose}>
					Zavřít
				</Button>
			</DialogActions>
		</Dialog>
	);
};
